import React, { useRef, useEffect } from 'react';
import { Box, useTheme } from '@mui/material';
import {CreateClassroom} from './DashboardContents/CreateInstance';
import AllCoursesList from './DashboardContents/TeacherCourse/AllCoursesList';
import TeacherList from './DashboardContents/UserList/TeacherList'
import StaffList from './DashboardContents/UserList/StaffList'
import StudentList from './DashboardContents/UserList/StudentList';
import StudentCard from './DashboardContents/StudentCard/StudentCard';
import TeacherAttendanceReview from './DashboardContents/TeacherAttendanceReview/TeacherAttendanceReview';
import TeacherSchedule from './DashboardContents/TeacherSchedule/TeacherSchedule';
import StudentSchedule from './DashboardContents/StudentSchedule/StudentSchedule';
import RevenueOverview from './DashboardContents/Accounting/RevenueOverview/RevenueOverview';
import OperationBrowser from './DashboardContents/Accounting/OperationBrowser/OperationBrowser';
import ExpenseRegister from './DashboardContents/Accounting/ExpenseRegister/ExpenseRegister';
import TeacherSettlements from './DashboardContents/Accounting/TeacherSettlements/TeacherSettlements';
import TeacherSalaryManagement from './DashboardContents/Accounting/TeacherSalaryManagement/TeacherSalaryManagement';
import ReceivablesTracker from './DashboardContents/Accounting/ReceivablesTracker/ReceivablesTracker';
import CashboxReconciliation from './DashboardContents/Accounting/CashboxReconciliation/CashboxReconciliation';
import BankReconciliation from './DashboardContents/Accounting/BankReconciliation/BankReconciliation';
import PersonalNotes from './DashboardContents/PersonalNotes/PersonalNotes';
import ColleagueNotes from './DashboardContents/ColleagueNotes/ColleagueNotes';
import SupportChat from '../support/SupportChat';
import StaffSupportPanel from '../support/StaffSupportPanel';
import EmailManagement from './DashboardContents/EmailManagement/EmailManagement';
import DailyReportSettings from './DashboardContents/DailyReportSettings/DailyReportSettings';
import InstituteInfo from './DashboardContents/InstituteInfo/InstituteInfo';
import SystemStatistics from './DashboardContents/Statistics/SystemStatistics';
import ClassroomInfoGraph from './DashboardContents/Statistics/ClassroomInfoGraph';
// import AdvancedFinancialAnalytics from './DashboardContents/Statistics/AdvancedFinancialAnalytics'; // TODO: Will be implemented in the future
import AssistantChat from './DashboardContents/Assistant/AssistantChat';
import DashboardWelcome from './DashboardWelcome';
import {useMenuContext} from '../../context/MenuContext';
import {useAuth} from '../../context/AuthContext';
import {canAccessSection} from '../../config/sectionAccess';

// Menu ids that render an actual section. Anything else (bare /dashboard,
// an unknown slug) falls back to the welcome/empty state.
const KNOWN_SECTIONS = [
    'teacher-list', 'student-list', 'staff-list', 'new-classroom', 'courses-list',
    'student-card', 'attendance-review', 'teacher-schedule', 'student-schedule', 'institution-accounting',
    'operation-browser', 'expense-register', 'teacher-accounting',
    'teacher-salary-management', 'student-accounting', 'cashbox-reconciliation',
    'bank-reconciliation', 'personal-notes', 'colleague-notes', 'support-chat', 'assistant',
    'staff-support-panel', 'emails', 'daily-report', 'institute-info', 'statistics', 'classroom-info-graph',
];

function Dashboard() {
    const theme = useTheme();
    const { selectedMenuItem } = useMenuContext();
    const { user } = useAuth();
    const scrollableContainerRef = useRef(null);
    // Sections are URL-addressable, so the sidebar's role filtering no longer
    // bounds what can be requested; re-check here and show the welcome screen
    // for sections this user's role doesn't get.
    const section = canAccessSection(user, selectedMenuItem) ? selectedMenuItem : '';
    const isAssistant = section === 'assistant';

    // Scroll to top when menu item changes
    useEffect(() => {
        if (scrollableContainerRef.current) {
            scrollableContainerRef.current.scrollTop = 0;
        }
        // Also scroll window to top as fallback
        window.scrollTo(0, 0);
    }, [selectedMenuItem]);

    return (
        <Box component="main" sx={{ flexGrow: 1, minWidth: 0, bgcolor: theme.palette.background.default, pt: '4rem' }}>
            <Box
                ref={scrollableContainerRef}
                sx={isAssistant ? {
                    // The assistant fills exactly the space under the fixed
                    // header; only its message list scrolls. Other sections
                    // keep their own scrolling container below.
                    m: { xs: 0, sm: '0 1rem' },
                    p: { xs: 0, sm: '1rem' },
                    height: 'calc(100dvh - 4rem)',
                    '@supports not (height: 100dvh)': { height: 'calc(100vh - 4rem)' },
                    overflow: 'hidden',
                    bgcolor: theme.palette.background.default,
                } : {
                    m: '0 2rem',
                    p: '1rem',
                    height: '90vh',
                    bgcolor: theme.palette.background.default,
                    overflowY: 'auto',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    touchAction: 'pan-x pan-y',
                    paddingBottom: '5rem',
                }}>
                    {!KNOWN_SECTIONS.includes(section) && <DashboardWelcome />}
                    {section === 'teacher-list' && <TeacherList />}
                    {section === 'student-list' && <StudentList />}
                    {section === 'staff-list' && <StaffList />}
                    {section === 'new-classroom' && <CreateClassroom />}
                    {section === 'courses-list' && <AllCoursesList />}
                    {section === 'student-card' && <StudentCard />}
                    {section === 'attendance-review' && <TeacherAttendanceReview />}
                    {section === 'teacher-schedule' && <TeacherSchedule />}
                    {section === 'student-schedule' && <StudentSchedule />}
                    {section === 'institution-accounting' && <RevenueOverview />}
                    {section === 'operation-browser' && <OperationBrowser />}
                    {section === 'expense-register' && <ExpenseRegister />}
                    {section === 'teacher-accounting' && <TeacherSettlements />}
                    {section === 'teacher-salary-management' && <TeacherSalaryManagement />}
                    {section === 'student-accounting' && <ReceivablesTracker />}
                    {section === 'cashbox-reconciliation' && <CashboxReconciliation />}
                    {section === 'bank-reconciliation' && <BankReconciliation />}
                    {section === 'personal-notes' && <PersonalNotes />}
                    {section === 'colleague-notes' && <ColleagueNotes />}
                    {section === 'assistant' && <AssistantChat />}
                    {section === 'support-chat' && <SupportChat />}
                    {section === 'staff-support-panel' && <StaffSupportPanel />}
                    {section === 'emails' && <EmailManagement />}
                    {section === 'daily-report' && <DailyReportSettings />}
                    {section === 'institute-info' && <InstituteInfo />}
                    {section === 'statistics' && <SystemStatistics />}
                    {section === 'classroom-info-graph' && <ClassroomInfoGraph />}
                    {/* {section === 'advanced-financial-analytics' && <AdvancedFinancialAnalytics />} // TODO: Will be implemented in the future */}
            </Box>
        </Box>
    );
}

export default Dashboard;
